import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageShell from '../../../components/PageShell';
import CardShell from '../../../components/CardShell';
import { useViewExcelExport } from '../../../components/ExcelExportButton';
import { useRegisterPageExports } from '../../../exports/pageExports';
import { useP } from '../../../i18n/useP';
import { usePersistentValue, usePersistentValueLocal } from '../../../hooks/usePersistentState';
import {
  getOfferMonths, getRealiseGrid, listSeasonProfiles, createSeasonProfile, updateSeasonProfile,
  deleteSeasonProfile, loadStandardProfiles,
} from '../../../api/salesOfferMonths';
import { listOffers, updateOffer, listOfferModels } from '../../../api/salesOffers';
import { getPlRevenueView } from '../../../api/financials_pl';
import OfferCreateDialog from '../OfferCreateDialog';
import SeasonProfilesPanel from '../SeasonProfilesPanel';
import { HEURES_JOUR } from '../offerUnits';
import { makeFmt } from './format';
import { MOIS_12, sumNN, realOffre } from './model';
import OfferTabs from './OfferTabs';
import OfferCard from './OfferCard';
import GlobalCard from './GlobalCard';
import AtterrissagePanel from './AtterrissagePanel';
import './PrevisionsPage.css';

/* ── Prévisions des ventes : l'écran A3 (décisions d'Olivier, 2026-10-21 et 2026-10-22 ; proto
   `WIP/protos/previsions-alt-a3v4-totaux.html`, brief `WIP/briefs/previsions-a3.md`).

   Une rangée de boutons tout en haut : la VUE GLOBALE (le chiffre d'affaires de toutes les offres), puis un
   bouton par offre qui ouvre sa grille seule. Dans la grille d'une offre, les mois clos portent le RÉALISÉ qu'on
   tape (mensuel ou cumulé), les mois à venir le PLANIFIÉ ; cinq totaux ferment la rangée. Le clic droit sur une
   offre (bouton du haut, ligne de la vue globale, nom dans la grille) et le stylo ouvrent la fenêtre de l'offre.
   La Synthèse garde la comparaison à un autre plan (OfferMonthPage). */

export default function PrevisionsPage({ activeTenant, year, addToast, canWrite = false, onGoRealise = null }) {
  const { t } = useTranslation();
  const p = useP();
  const titre = t('nav:items.sales-backlog');
  const tid = activeTenant?.id;
  const cle = tid || 'none';
  /* Les choix de lecture se retiennent pour la session, par client. */
  const [choixMem, setChoix] = usePersistentValue(`pv_choix_${cle}`, 'all');         // all · identifiant d'offre
  const [saisie, setSaisie] = usePersistentValue(`pv_saisie_${cle}`, 'mensuel');       // mensuel · cumul (du réalisé)
  const [valeurs, setValeurs] = usePersistentValue(`pv_valeurs_${cle}`, 'mensuel');   // mensuel · cumul (valeurs de la synthèse)
  const [flux, setFlux] = usePersistentValue(`pv_flux_${cle}`, 'tot');                 // tot · bk · new
  const [acc, setAcc] = usePersistentValue(`pv_acc_${cle}`, false);                    // une ligne à la fois
  /* L'atterrissage se libère comme le menu : la punaise le garde ou le libère, le choix reste dans le navigateur. */
  const [libre, setLibre] = usePersistentValueLocal('om_atterrissage_libre', false);
  const [view, setView] = useState(null);
  const [erreur, setErreur] = useState(null);           // le serveur n'a pas répondu : on le dit au lieu de charger sans fin
  const [realise, setRealise] = useState(null);
  const [profils, setProfils] = useState([]);
  const [unit, setUnit] = useState('kchf');
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);
  const [exp, setExp] = useState(new Set());               // lignes dépliées (clé de ligne)
  const [ouvertes, setOuvertes] = useState(new Set());     // vue globale : offres dont les lignes se lisent
  const [ligneChoisie, setLigneChoisie] = useState(null);
  const [menu, setMenu] = useState(null);                  // clic droit : { x, y, r }
  const [edition, setEdition] = useState(null);            // la fenêtre d'une offre
  const [profilsOuverts, setProfilsOuverts] = useState(false);
  const [caReel, setCaReel] = useState(null);
  const [reprise, setReprise] = useState(null);
  const [unitesAff, setUnitesAff] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem(`om_unites_${cle}`) || '{}'); } catch (e) { return {}; }
  });
  const exportRef = useRef(null);

  const toast = useCallback((m, type) => { if (addToast) addToast(m, type); }, [addToast]);
  const reload = useCallback(async () => {
    if (!tid || !year) return;
    const [r, re, pr] = await Promise.all([getOfferMonths(tid, year, 1, null), getRealiseGrid(tid, year), listSeasonProfiles(tid)]);
    if (r.error) { toast(r.error, 'error'); setErreur(r.error); return; }
    setErreur(null);
    setView(r.data);
    setRealise((re && re.data) || null);
    setProfils((pr && pr.data) || []);
  }, [tid, year, toast]);
  useEffect(() => { reload(); }, [reload]);

  /* Le CA de la comptabilité, quand Finance répond : l'écart se lit sous le total de la vue globale. */
  useEffect(() => {
    if (!tid || !year) return undefined;
    let vivant = true;
    getPlRevenueView(tid, year)
      .then((r) => {
        const rows = (r && r.data && r.data.rows) || [];
        const tot = rows.find((x) => x.type === 'section_total' && x.section === 'revenue');
        const lignes = (r && r.data && r.data.lines) || [];
        const ligne = lignes.find((x) => x.line_role === 'advances_release');
        const rr = ligne ? rows.find((x) => x.type === 'detail' && x.code === ligne.id) : null;
        if (vivant) {
          setCaReel(tot && tot.total_ytd != null ? Number(tot.total_ytd) : null);
          setReprise(rr && rr.total != null ? Number(rr.total) : null);
        }
      })
      .catch(() => { if (vivant) { setCaReel(null); setReprise(null); } });
    return () => { vivant = false; };
  }, [tid, year]);

  const excel = useViewExcelExport({ ref: exportRef, sheetName: titre, fileBase: titre, year, addToast });
  const excelRun = useRef(null); excelRun.current = excel.run;
  const exportOptions = useMemo(() => [{ id: 'excel', label: 'Excel', run: () => excelRun.current && excelRun.current() }], []);
  useRegisterPageExports(exportOptions);

  const appel = useCallback(async (promesse) => {
    const r = await promesse;
    if (r && r.error) toast(r.error, 'error');
    await reload();
  }, [reload, toast]);

  /* ── La fenêtre de l'offre ─────────────────────────────────────────── */
  const ouvrirOffre = async (r) => {
    const [o, m, pl] = await Promise.all([listOffers(tid), listOfferModels(tid), getPlRevenueView(tid, year)]);
    if (o.error || m.error) { toast(o.error || m.error, 'error'); return; }
    const offre = (o.data || []).find((x) => x.id === r.offer_id);
    if (!offre) return;
    const rows = pl?.data?.rows || [];
    const seen = new Set();
    const lines = rows.filter((x) => x.type === 'detail' && x.section === 'revenue' && !seen.has(x.label) && seen.add(x.label))
      .map((x) => ({ value: x.code, label: x.label }));
    setEdition({ offre, modeles: m.data || [], lines });
  };
  const modifierOffre = async (champs) => {
    const r = await updateOffer(tid, edition.offre.id, champs);
    if (r.error) { toast(r.error, 'error'); return; }
    setEdition(null);
    await reload();
  };

  /* Le menu du clic droit : « Modifier l'offre… » (rien à ouvrir en lecture seule). */
  const ouvrirMenu = (e, r) => {
    if (!canWrite) return;
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY, r });
  };
  useEffect(() => {
    if (!menu) return undefined;
    const fermer = () => setMenu(null);
    const touche = (e) => { if (e.key === 'Escape') setMenu(null); };
    window.addEventListener('click', fermer);
    window.addEventListener('scroll', fermer, true);
    window.addEventListener('keydown', touche);
    return () => {
      window.removeEventListener('click', fermer);
      window.removeEventListener('scroll', fermer, true);
      window.removeEventListener('keydown', touche);
    };
  }, [menu]);

  /* ── Les nombres ───────────────────────────────────────────────────── */
  const fmt = useMemo(() => makeFmt(unit), [unit]);
  const dev = unit === 'kchf' ? 'kCHF' : 'CHF';
  const temps = (o) => o.equation === 'time_rate' && ['h', 'jh'].includes(o.unit);
  const uniteAff = (o) => (temps(o) ? (unitesAff[o.offer_id] || o.unit) : o.unit);
  /* Le facteur affiché / stocké d'un volume : heures ⇄ jours-hommes. */
  const facteur = (o) => (!temps(o) || uniteAff(o) === o.unit ? 1 : o.unit === 'h' ? 1 / HEURES_JOUR : HEURES_JOUR);
  const basculerUnite = (o) => setUnitesAff((x) => {
    const n = { ...x, [o.offer_id]: uniteAff(o) === 'h' ? 'jh' : 'h' };
    try { window.localStorage.setItem(`om_unites_${cle}`, JSON.stringify(n)); } catch (e) { /* sans mémoire, tant pis */ }
    return n;
  });

  const vide = view && view.rows.length === 0;
  const choix = view && choixMem !== 'all' && view.rows.some((r) => r.offer_id === choixMem) ? choixMem : 'all';
  const offre = view && choix !== 'all' ? view.rows.find((r) => r.offer_id === choix) : null;
  const choisir = (id, ligne = null) => { setChoix(id); setLigneChoisie(ligne); };
  const basculerAcc = () => { setAcc(!acc); setExp(new Set()); };

  /* L'écart avec la comptabilité : le CA comptable à fin du dernier mois clos moins le CA réalisé des offres. */
  const ouvertsMois = useMemo(() => (view?.open || []).filter((i) => i < 12), [view]);
  const closMois = useMemo(() => MOIS_12.filter((i) => !ouvertsMois.includes(i)), [ouvertsMois]);
  const ecartCompta = view && closMois.length && caReel != null
    ? caReel - view.rows.reduce((a, r) => a + (sumNN(realOffre(realise, r).ca, closMois) || 0), 0)
    : null;

  const c = {
    p, tid, year, canWrite, fmt, unit, dev, vue: view, realise, saisie, flux, acc, exp, setExp, appel, toast,
    recharger: reload, facteur, uniteAff, temps, basculerUnite, profils, valeurs, setValeurs, setSaisie, setFlux, ouvrirMenu, ouvrirOffre, choisir, choix, ligneChoisie,
    onGoRealise, basculerAcc, ouvertes, setOuvertes, ecartCompta, caReel, reprise, libre, setLibre,
    ouvrirProfils: canWrite || profils.length ? () => setProfilsOuverts(true) : null,
  };

  const profilAppel = async (promesse) => {
    const r = await promesse;
    if (r && r.error) { toast(r.error, 'error'); return; }
    await reload();
  };

  if (!tid) {
    return <PageShell framed title={titre}><p className="om-empty">{p('Choisir un client.')}</p></PageShell>;
  }
  const corps = !view ? (erreur ? (
    <div className="om-empty" role="alert">
      {p('Les prévisions ne se chargent pas')} : {erreur}{' '}
      <button type="button" className="lk-btn" onClick={reload}>{p('Réessayer')}</button>
    </div>
  ) : <div className="om-empty">{p('Chargement…')}</div>) : vide ? <div className="om-empty">{p('Aucune offre active.')}</div>
    : profilsOuverts ? (
      <CardShell title={p('Profils de saisonnalité')} flush
                 primaryAction={(
                   <>
                     <button type="button" className="lk-btn lk-btn--quiet" onClick={() => setProfilsOuverts(false)}>
                       {p('Fermer les profils')} ✕
                     </button>
                     {canWrite && (
                       <>
                         <button type="button" className="lk-btn lk-btn--quiet" onClick={async () => {
                           const r = await loadStandardProfiles(tid);
                           if (r && r.error) { toast(r.error, 'error'); return; }
                           if (r && r.data && r.data.added === 0) toast(p('Les profils de base sont déjà tous chargés.'));
                           await reload();
                         }}>
                           {p('Charger les profils de base')}
                         </button>
                         <button type="button" className="lk-btn" onClick={() => profilAppel(createSeasonProfile(tid, { weights: Array(12).fill(100) }))}>
                           ＋ {p('Profil')}
                         </button>
                       </>
                     )}
                   </>
                 )}>
        <SeasonProfilesPanel profils={profils} canWrite={canWrite}
                             onUpdate={(id, data) => profilAppel(updateSeasonProfile(tid, id, data))}
                             onDelete={(id) => profilAppel(deleteSeasonProfile(tid, id))}
                             onMove={(id, sens) => profilAppel(updateSeasonProfile(tid, id, { move: sens }))} />
      </CardShell>
    ) : (
      <>
        <OfferTabs c={c} />
        <div className={`om-grille pv-ecran${libre ? ' om-grille--libre' : ''}`}>
          <div className="om-grille__g">
            {offre ? <OfferCard key={offre.offer_id} c={c} r={offre} /> : <GlobalCard c={c} />}
          </div>
          <AtterrissagePanel c={c} />
        </div>
      </>
    );
  return (
    <PageShell framed title={titre} exportRef={exportRef} unit={pageUnit}>
      {corps}
      {menu && (
        <div className="pv-ctx" role="menu" style={{ left: Math.min(menu.x, Math.max(0, window.innerWidth - 200)), top: menu.y }}>
          <button type="button" role="menuitem" onClick={() => ouvrirOffre(menu.r)}>{p("Modifier l'offre…")}</button>
        </div>
      )}
      {edition && <OfferCreateDialog modeles={edition.modeles} lines={edition.lines} offre={edition.offre}
                                     onCreate={modifierOffre} onClose={() => setEdition(null)} />}
    </PageShell>
  );
}
